import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GoogleLogo, Lock, EnvelopeSimple } from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await client.post("/auth/forgot-password", { email });
        toast.success("If that email has an account, a reset link is on its way.");
        setMode("login");
      } else {
        const r = await client.post("/auth/login", { email, password });
        login(r.data.token, r.data.user);
        nav("/");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fieldBase = "w-full h-12 pl-11 pr-4 bg-white text-[#2A0A0A] placeholder:text-[#8A8681] text-[15px] border border-white/10 rounded-[4px] outline-none focus:ring-2 focus:ring-[#B08D57] focus:border-[#B08D57] transition-shadow";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#150909]">
      {/* LEFT — dark oxblood panel: brand, heading & form */}
      <div className="relative flex flex-col justify-between px-8 sm:px-14 lg:px-16 py-10 lg:py-12 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[#150909]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_8%,rgba(74,0,0,0.75),transparent_58%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_92%,rgba(176,141,87,0.10),transparent_46%)]" />
        </div>

        {/* Brand lockup — left-aligned with the form below */}
        <div className="relative z-10 w-full max-w-md" data-testid="login-brand">
          <img src="/reelty-r.png" alt="Reelty" className="h-14 w-auto object-contain opacity-55 -ml-2.5" />
        </div>

        {/* Heading + form */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}
          className="relative z-10 w-full max-w-md py-10">
          {mode === "forgot" ? (
            <h1 className="font-black text-[2.4rem] sm:text-[2.75rem] leading-[1.05] tracking-tight text-[#FBFAF7]">Reset your password</h1>
          ) : (
            <div data-testid="login-heading">
              <span className="gold-rule block w-14 mb-5" />
              <h1 className="tracking-tight text-[#FBFAF7]">
                <span className="block font-medium text-[1.4rem] sm:text-[1.6rem] leading-tight text-[#B08D57]">Welcome to your</span>
                <span className="block font-black text-[clamp(2rem,8vw,3.5rem)] tracking-tight leading-[0.95] mt-1 text-[#FBFAF7] whitespace-nowrap">Reelty Portal</span>
              </h1>
            </div>
          )}

          <div className="mt-8 space-y-3">
            <Button data-testid="google-login-button" type="button" onClick={googleLogin}
              className="w-full h-12 bg-white text-[#2A0A0A] hover:bg-[#F2ECE0] rounded-[4px] font-semibold">
              <GoogleLogo size={20} weight="bold" className="mr-2 text-[#4A0000]" /> Continue with Google
            </Button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-white/12" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/35">or</span>
              <div className="flex-1 h-px bg-white/12" />
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="login-form">
              <div className="relative">
                <EnvelopeSimple size={18} weight="duotone" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4A0000]" />
                <input data-testid="login-email-input" type="email" required placeholder="you@studio.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} className={fieldBase} />
              </div>
              {mode === "login" && (
                <div className="relative">
                  <Lock size={18} weight="duotone" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4A0000]" />
                  <input data-testid="login-password-input" type="password" required placeholder="Password"
                    value={password} onChange={(e) => setPassword(e.target.value)} className={fieldBase} />
                </div>
              )}
              <Button data-testid="login-submit-button" type="submit" disabled={loading}
                className="group w-full h-12 bg-[#4A0000] hover:bg-[#5C1A1A] text-[#FBFAF7] rounded-[4px] font-semibold uppercase tracking-[0.12em] text-[13px] shadow-[0_14px_34px_-12px_rgba(74,0,0,0.9)]">
                {loading ? "Please wait…" : mode === "forgot" ? "Email me a reset link" : "Sign in"}
                {!loading && <ArrowRight size={17} className="ml-2 transition-transform group-hover:translate-x-0.5" />}
              </Button>
            </form>

            <div className="flex items-center justify-between text-sm pt-1">
              <button data-testid="toggle-forgot" onClick={() => setMode(mode === "forgot" ? "login" : "forgot")}
                className="text-[#FBFAF7]/70 hover:text-[#B08D57] inline-flex items-center gap-1.5 transition-colors">
                <Lock size={14} weight="duotone" />{mode === "forgot" ? "Back to sign in" : "Forgot password?"}
              </button>
              <a href="mailto:support@goreelty.com" data-testid="support-note"
                className="text-[#FBFAF7]/45 hover:text-[#B08D57] transition-colors">Need access?</a>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.14em] leading-relaxed text-[#FBFAF7]/40">
            New here?{" "}
            <a href="/book" data-testid="public-book-link" className="text-[#B08D57] hover:text-[#C9A96A] transition-colors">Book a Shoot</a>
            {" "}— no account needed
          </p>
        </div>
      </div>

      {/* RIGHT — clear cinematic property image */}
      <div className="hidden lg:block relative overflow-hidden" data-testid="login-hero-image">
        <img src={HERO_IMG} alt="Luxury property interior" className="absolute inset-0 w-full h-full object-cover kenburns-out" />
        {/* minimal overlay — just blends the seam into the dark panel */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#150909]/45 via-transparent to-transparent" />
      </div>
    </div>
  );
}
